import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Renderer2,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngxs/store';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { SanitizedHtmlPipe } from '../../pipes/sanitized-html-pipe';
import { ReceiveNotificationMessage } from '../../services/signal-r.service';
import { SignalrNotificationService } from '../../services/signalr-notification.service';

type ListenerCleanup = () => void;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, SanitizedHtmlPipe],
  selector: 'orion-platform-signalr-notification',
  standalone: true,
  styleUrl: './signalr-notification.component.scss',
  templateUrl: './signalr-notification.component.html',
})
export class SignalrNotificationComponent {
  /** Delay used to ensure the view is rendered before attaching click handlers. */
  private static readonly ATTACH_LISTENERS_DELAY_MS = 200;

  /** Duration after which the notification is auto-dismissed. */
  private static readonly AUTO_DISMISS_MS = 4000;

  private readonly notificationService = inject(SignalrNotificationService);
  private readonly store = inject(Store);
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  /** Notifications currently displayed in the view.*/
  notifications: ReceiveNotificationMessage[] = [];

  /** Per-notification cleanup callbacks (renderer listeners). */
  private readonly listenerCleanups = new Map<string, ListenerCleanup[]>();

  constructor() {
    this.notificationService
      .getAlert()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((alert): void => this.onAlert(alert));
  }

  /** Removes a notification from the UI (manual dismiss). */
  /**
   * Handles new alerts coming from the notification service.
   * @param alert The newly received notification, or `undefined` if the stream emitted no value.
   */
  private onAlert(alert?: ReceiveNotificationMessage): void {
    // Original behavior: clear any existing notification + listeners before showing a new one.
    this.clearAll();

    // If no alert (undefined/null), nothing to show.
    if (!alert) return;

    // Prepare/transform message HTML (adds link markup/events in the string).
    alert.ShortMessage = Helper.addLinkEventsOnHtmlString(alert.ShortMessage);

    // Show the notification.
    this.notifications = [alert];
    this.cdr.markForCheck();

    // Attach click listeners *after* the view has rendered.
    this.attachTemplateListenersAfterRender(alert);

    // Auto-dismiss after a fixed time.
    this.scheduleAutoDismiss(alert);
  }

  /**
   * Attaches click handlers to link elements inside the rendered notification template.
   * @param alert The notification for which we attach listeners.
   */
  private attachTemplateListenersAfterRender(alert: ReceiveNotificationMessage): void {
    window.setTimeout((): void => {
      const listeners =
        Helper.setClickEventsOnInnerTemplate(
          this.store,
          this.document,
          this.elRef,
          this.renderer,
        ) ?? [];

      // Track cleanup so we can remove listeners when dismissed.
      this.listenerCleanups.set(alert.Id, listeners);

      Helper.detectChanges(this.cdr);
    }, SignalrNotificationComponent.ATTACH_LISTENERS_DELAY_MS);
  }

  /**
   * Schedules auto-dismiss for the given notification.
   * @param alert The notification to dismiss after the timeout.
   */
  private scheduleAutoDismiss(alert: ReceiveNotificationMessage): void {
    window.setTimeout(
      (): void => this.dismiss(alert),
      SignalrNotificationComponent.AUTO_DISMISS_MS,
    );
  }

  /**
   * Dismisses a notification.
   * @param alert The notification to dismiss.
   */
  private dismiss(alert: ReceiveNotificationMessage): void {
    // Clean up renderer listeners tied to this alert.
    this.cleanupListeners(alert.Id);

    // Remove from UI.
    this.notifications = this.notifications.filter((x): boolean => x !== alert);
    this.cdr.markForCheck();
  }

  /** Clears all notifications and destroys all registered renderer listeners.*/
  private clearAll(): void {
    for (const id of this.listenerCleanups.keys()) {
      this.cleanupListeners(id);
    }

    this.listenerCleanups.clear();
    this.notifications = [];
    this.cdr.markForCheck();
  }

  /**
   * Removes renderer listeners for a specific notification id.
   * @param notificationId Notification identifier used as the key for stored cleanups.
   */
  private cleanupListeners(notificationId: string): void {
    const cleanups = this.listenerCleanups.get(notificationId);
    if (!cleanups?.length) return;

    Helper.destroyRendererListener(cleanups);
    this.listenerCleanups.delete(notificationId);
  }
}
